import CtaBanner from '@src/components/CtaBanner';
import FeedbackSection from '@src/components/feedbackSection';
import Layout from '@src/components/layout/layout';
import PageSlugs from '@src/components/layout/pageSlugs';
import FeatureSection from '@src/components/sections/featureSection/featureSection';
import HowItWorksSection from '@src/components/sections/howItWorksSection/howItWorksSection';
import { getHowItWorksSectionViewModel } from '@src/components/sections/howItWorksSection/howItWorksSectionMapper';
import PageBanner from '@src/components/sections/pageBanner/pageBanner';
import TeacherSection from '@src/components/teacherSection';
import api from '@src/utils/api.js';
import {
  convertToPercentage,
  currencyToSymbol,
  getSiteCurrency,
} from '@src/utils/pricing';
import { Link, graphql } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'theme-ui';

const loadData = async ({ subjectId }) => {
  const response = {
    bestTeachers: null,
    feedback: null,
  };

  const loadTeachers = api.get(
    `teachers?${new URLSearchParams({
      limit: 6,
      subject: subjectId,
      currency: getSiteCurrency(),
    })}`
  );

  const loadFeedback = api.get(
    `ratings?${new URLSearchParams({
      limit: 12,
      minimumRating: 4,
    })}`
  );

  let teachers = [];
  let feedback = [];
  try {
    const [teachersRes, feedbackRes] = await Promise.all([
      loadTeachers,
      loadFeedback,
    ]);
    teachers = teachersRes.data.filter(
      (t) => t.available_slots && t.available_slots > 0
    );
    feedback = feedbackRes;
  } catch (error) {
    console.log(error);
  }

  response.bestTeachers = teachers?.map((t) => {
    const description = t.description || '';

    return {
      id: t.userId,
      slug: t.tag,
      avatarUrl: t.avatarUrl,
      firstName: t.firstName,
      description:
        description.length > 220
          ? description.slice(0, 220) + '...'
          : description,
      videoUrl: t.videoUrl,
      // subject: teacher.subject,
      price: `${(Math.round(t.lessonprice * 100) / 100).toFixed(2)} ${
        currencyToSymbol[t.currency]
      }`,
      priceNumber: (Math.round(t.lessonprice * 100) / 100).toFixed(2),
      rating: convertToPercentage(t.rating, 5),
      ratingCount: t.ratingCount,
      isFeatured: t.isFeatured,
      currency: currencyToSymbol[t.currency],
      lessonCount: t.lessonCount,
    };
  });

  response.feedback = feedback.data.map((f) => {
    return {
      id: f.id,
      author: f.byUserDisplayName,
      text: f.comment.slice(0, 220),
      rating: convertToPercentage(f.rating, 5),
    };
  });

  return response;
};

export const query = graphql`
  query SubjectPageBySlug($slug: String!) {
    contentfulMokomasisDalykas(slug: { eq: $slug }) {
      slug
      node_locale
      name
      heading
      seoTitle
      seoDescription
      description {
        childMarkdownRemark {
          htmlAst
        }
      }
      image {
        color: gatsbyImageData(
          backgroundColor: ""
          quality: 100
          formats: [AUTO, WEBP]
        )
        image: gatsbyImageData(
          quality: 100
          placeholder: BLURRED
          formats: [AUTO, WEBP]
        )
      }
      features {
        title
        description {
          childMarkdownRemark {
            htmlAst
          }
        }
        icon {
          file {
            url
          }
        }
      }
      faq {
        title
        questions {
          question
          answer {
            childMarkdownRemark {
              htmlAst
            }
          }
        }
      }
      banner {
        title
        description {
          childMarkdownRemark {
            htmlAst
          }
        }
        image {
          gatsbyImageData(
            width: 493
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
        buttonLink
        buttonText
      }
      headingRecomendedTeachers
      sections {
        ...HowItWorksSection
      }
    }
  }
`;

const SubjectPageTemplate = ({ data, pageContext }) => {
  const { t, i18n } = useTranslation();
  const [bestTeachers, setBestTeachers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const pageData = data.contentfulMokomasisDalykas;

  useEffect(() => {
    if (i18n.language !== pageContext.locale) {
      i18n.changeLanguage(pageContext.locale);
    }
  }, []);

  const howItWorksSectionViewModel = getHowItWorksSectionViewModel(
    pageData?.sections
  );

  useEffect(() => {
    const load = async () => {
      const { bestTeachers, feedback } = await loadData({
        subjectId: pageContext.apiId,
      });

      setBestTeachers(bestTeachers);
      setFeedback(feedback);
    };

    load();
  }, []);

  return (
    <Layout
      locale={pageContext.locale}
      otherSlugs={pageContext.otherSlugs}
      currentSlug={pageContext.slug}
      seoTitle={pageData.seoTitle}
      seoDescription={pageData.seoDescription}
    >
      <PageBanner
        imageData={pageData.image?.image}
        bgColor={pageData.image?.color?.backgroundColor}
        title={pageData.heading}
        description={pageData.description?.childMarkdownRemark?.htmlAst}
      >
        <Link to={`${PageSlugs.subjectsSearch[i18n.language]}`}>
          <Button>{t('chooseATeacher')}</Button>
        </Link>
      </PageBanner>

      {/* <Box>
        <Container
          p={4}
          sx={{ maxWidth: '860px', margin: 'auto', textAlign: 'center' }}
        >
          <Box sx={{ fontSize: 3 }}>
            {renderHtmlToReact(
              pageData.description.childMarkdownRemark.htmlAst
            )}
          </Box>
        </Container>
      </Box> */}

      <FeatureSection features={pageData.features} />

      <TeacherSection
        title={pageData.headingRecomendedTeachers}
        bgColor="primaryPaleBg"
        teachers={bestTeachers}
        linkToTeachersPage={PageSlugs.subjectsSearch[i18n.language]}
      />

      <HowItWorksSection vm={howItWorksSectionViewModel} />

      {/* <DukSection
        title={t('frequentlyAskedQuestions')}
        questions={pageData.faq.questions}
        showButtonToDuk
      /> */}

      <FeedbackSection
        variant="dark"
        title={t('studentFeedback')}
        feedback={feedback}
        quoteColor="#fff8e0"
      />

      <CtaBanner
        variant="secondary"
        title={pageData.banner.title}
        splitVerticalBgColor={feedback.length ? 'primaryPaleBg' : ''}
        description={pageData.banner.description.childMarkdownRemark.htmlAst}
        buttonText={pageData.banner.buttonText}
        buttonLink={pageData.banner.buttonLink}
        image={pageData.banner.image}
        svgElements
      />
    </Layout>
  );
};

export default SubjectPageTemplate;
